import GuideLayout, {
  H2,
  H3,
  P,
  UL,
  OL,
  Callout,
  ExternalLink,
  Strong,
  Em,
} from "../ui/GuideLayout.jsx";

export default function EncontrarMoradia() {
  return (
    <GuideLayout
      title="Como encontrar apartamento (apato) no Japão"
      subtitle="Imobiliárias amigas de brasileiro, o sistema do shikikin e reikin, contratos, e como evitar a recusa por ser estrangeiro."
      slug="encontrar-moradia-apato-japao"
      description="Guia para alugar apartamento (apato) no Japão sendo brasileiro. Documentos, sikkikin, reikin, imobiliárias amigas, evitar discriminação."
      updated="Maio de 2026"
      readTime="11 min"
    >
      <P>
        Encontrar apartamento no Japão sendo brasileiro é uma das partes mais
        frustrantes da experiência. Tem regras estranhas, custos altos pra
        começar, e a famosa recusa &ldquo;não alugamos para estrangeiros&rdquo;
        ainda acontece em muitas imobiliárias.
      </P>
      <P>
        Este guia explica como o sistema funciona, quanto custa, e onde
        procurar para evitar discriminação.
      </P>

      <H2 id="opcoes">Opções de moradia</H2>
      <UL>
        <li>
          <Strong>Apartamento da empreiteira:</Strong> a maioria dos
          brasileiros começa aqui. Empreiteira já tem o apato negociado, te
          coloca lá e desconta do salário. Sem entrada, mas frequentemente
          caro e quarto compartilhado.
        </li>
        <li>
          <Strong>Apato (アパート):</Strong> apartamento em prédio de 2-3
          andares, construído em madeira ou estrutura leve. Mais barato,
          paredes finas, comum no interior.
        </li>
        <li>
          <Strong>Manshon (マンション):</Strong> apartamento em prédio de
          concreto com 4+ andares. Mais caro, mais sólido, com elevador,
          isolamento térmico melhor.
        </li>
        <li>
          <Strong>UR Toshikiko (UR都市機構):</Strong> programa habitacional do
          governo. Sem shikikin/reikin/fiador. Aceita estrangeiro. Procurado.
        </li>
        <li>
          <Strong>Casa (一戸建て / ikkodate):</Strong> casa térrea/sobrado.
          Geralmente compra, mas existe aluguel em algumas regiões.
        </li>
      </UL>

      <H2 id="custo-inicial">O custo inicial assustador</H2>
      <P>
        Para alugar apato no Japão pelo método tradicional, você paga na
        assinatura:
      </P>
      <UL>
        <li>
          <Strong>Shikikin</Strong> (敷金 — depósito caução): 1-2 meses de
          aluguel. Devolvido no fim do contrato menos eventuais reparos.
        </li>
        <li>
          <Strong>Reikin</Strong> (礼金 — &ldquo;dinheiro de presente&rdquo;):
          1-2 meses de aluguel. <Strong>Não devolvido</Strong> — é literal
          presente pro proprietário, tradição japonesa.
        </li>
        <li>
          <Strong>Primeiro mês adiantado</Strong>: 1 aluguel
        </li>
        <li>
          <Strong>Taxa da imobiliária (chukaite suuryou):</Strong> 0,5 a 1 mês
          de aluguel + 10% imposto
        </li>
        <li>
          <Strong>Seguro contra incêndio</Strong>: ¥15.000–25.000 por 2 anos
        </li>
        <li>
          <Strong>Trocar fechadura</Strong>: ¥15.000–25.000
        </li>
        <li>
          <Strong>Garantidora</Strong> (alternativa ao fiador): ¥20.000–50.000
          + renovação anual de ~¥10.000
        </li>
      </UL>
      <P>
        <Strong>Exemplo real:</Strong> apato de ¥50.000/mês de aluguel:
      </P>
      <UL>
        <li>Shikikin: ¥100.000</li>
        <li>Reikin: ¥50.000</li>
        <li>Primeiro mês: ¥50.000</li>
        <li>Taxa imobiliária: ¥55.000</li>
        <li>Seguro: ¥20.000</li>
        <li>Fechadura: ¥18.000</li>
        <li>Garantidora: ¥40.000</li>
        <li><Strong>Total para entrar: ¥333.000</Strong> (R$ 13.000 a R$ 14.000)</li>
      </UL>
      <Callout kind="warn">
        Esse custo é o motivo pelo qual quase todo brasileiro fica no apato
        da empreiteira por meses. Não tem como começar com ¥333.000 de
        entrada antes mesmo do primeiro salário.
      </Callout>

      <H2 id="como-reduzir">Como reduzir o custo inicial</H2>

      <H3>1. UR (公団 / koudan)</H3>
      <P>
        Programa habitacional público. Sem shikikin, sem reikin, sem fiador,
        sem taxa da imobiliária. Você paga só primeiro mês + 1 mês de
        caução simbólica.
      </P>
      <UL>
        <li>Aceita estrangeiros com zairyu válido</li>
        <li>Demanda renda mínima (geralmente 4x o aluguel)</li>
        <li>Lista de espera em algumas regiões</li>
        <li>Site: <ExternalLink href="https://www.ur-net.go.jp/">ur-net.go.jp</ExternalLink></li>
      </UL>

      <H3>2. Apato &ldquo;zero-zero&rdquo; (敷金礼金ゼロ)</H3>
      <P>
        Apatos onde o proprietário renunciou ao shikikin e reikin. Você só
        paga primeiro mês + taxa da imobiliária + garantidora. Custo inicial
        cai pra ¥80.000–150.000.
      </P>
      <P>
        Geralmente são apatos mais antigos, em regiões com excesso de
        oferta. Não é pior necessariamente, só menos competitivo.
      </P>

      <H3>3. Sharehouse (シェアハウス)</H3>
      <P>
        Casas/apatos compartilhados estilo república. Quarto individual,
        cozinha e banheiro compartilhados. Custo inicial: ¥30.000–80.000.
      </P>
      <UL>
        <li>Aluguel: ¥40.000–80.000/mês incluindo contas</li>
        <li>Sem fiador necessário em quase todas</li>
        <li>Aceita estrangeiros — muitas focam em estrangeiros</li>
        <li>
          Sites: <ExternalLink href="https://www.oakhouse.jp/">Oak House</ExternalLink>,{" "}
          <ExternalLink href="https://www.sakura-house.com/">Sakura House</ExternalLink>
        </li>
      </UL>

      <H3>4. Sub-aluguel com brasileiro</H3>
      <P>
        Algum amigo brasileiro com apato próprio aluga um quarto pra você.
        Sem contrato formal, custo inicial zero. Não é legal tecnicamente
        (vai contra o contrato dele), mas é muito comum.
      </P>

      <H2 id="imobiliarias-amigas">Imobiliárias amigas de brasileiro</H2>
      <P>
        Algumas imobiliárias têm relacionamento com a comunidade brasileira,
        atendem em português, e não recusam por estrangeiro. Procure por
        &ldquo;imobiliária brasileira&rdquo; + sua cidade no Google ou
        Facebook.
      </P>
      <P>
        Algumas redes maiores que costumam aceitar bem:
      </P>
      <UL>
        <li>
          <Strong>Leopalace21:</Strong> rede grande, atende estrangeiro, tem
          apato semi-mobiliado, contrato curto
        </li>
        <li>
          <Strong>Able (アパマンショップ é da mesma rede):</Strong> rede
          grande, algumas filiais têm atendente que fala inglês
        </li>
        <li>
          <Strong>Mini-Mini:</Strong> bem distribuída no Japão
        </li>
        <li>
          <Strong>Apaman:</Strong> idem
        </li>
        <li>
          <Strong>Imobiliárias brasileiras locais:</Strong> &ldquo;Conexão
          Brasil&rdquo;, &ldquo;Latino Imóveis&rdquo;, &ldquo;Família
          Imóveis&rdquo;, etc. — variam por região
        </li>
      </UL>
      <Callout>
        Procure no <Strong>Facebook Marketplace</Strong> ou grupos como
        &ldquo;Brasileiros em [sua cidade]&rdquo; — frequentemente brasileiros
        repassando apato ou indicando imobiliárias que aceitam bem.
      </Callout>

      <H2 id="documentos">Documentos necessários</H2>
      <OL>
        <li>
          <Strong>Zairyu card</Strong> (com 1+ ano de validade restante de
          preferência — proprietário gosta de saber que você vai ficar)
        </li>
        <li><Strong>Passaporte</Strong></li>
        <li>
          <Strong>Juuminhyou</Strong> (住民票) atual
        </li>
        <li>
          <Strong>Comprovante de renda</Strong>: certificado da empresa
          (在職証明書), 2-3 últimos contracheques, gensen choshuhyo do ano anterior
        </li>
        <li>
          <Strong>Inkan</Strong>
        </li>
        <li>
          <Strong>Fiador</Strong> ou garantidora (geralmente garantidora)
        </li>
        <li>
          <Strong>My Number</Strong> (alguns proprietários pedem)
        </li>
      </OL>

      <H2 id="garantidora">Como funciona a garantidora</H2>
      <P>
        Tradicionalmente, no Japão você precisava de um <Em>hoshouin</Em>{" "}
        (保証人 — fiador), geralmente japonês com renda boa. Pra
        estrangeiro recém-chegado isso é quase impossível.
      </P>
      <P>
        Hoje quase todos usam <Em>hoshou gaisha</Em> (保証会社 — empresa
        garantidora). Você paga uma taxa inicial (¥20.000–60.000) + renovação
        anual (~¥10.000), e a empresa cobre o aluguel se você não pagar.
      </P>
      <UL>
        <li>Empresas comuns: Nikkou Hoshou, Casa, Aplus, Recoco</li>
        <li>Aprovação demora 1-3 dias úteis</li>
        <li>Eles checam histórico, renda, status de visto</li>
        <li>
          <Strong>Atraso de aluguel é registrado:</Strong> 2-3 atrasos te
          colocam em black list que outras garantidoras consultam
        </li>
      </UL>

      <H2 id="recusa-estrangeiro">A famosa recusa de estrangeiro</H2>
      <P>
        Acontece. Algumas imobiliárias e proprietários simplesmente recusam.
        Motivos comuns que eles dão:
      </P>
      <UL>
        <li>&ldquo;O proprietário prefere japonês&rdquo;</li>
        <li>&ldquo;Não temos contrato em inglês&rdquo;</li>
        <li>&ldquo;Já estamos cheios&rdquo; (mas o apato está vazio)</li>
      </UL>
      <P>
        Como lidar:
      </P>
      <UL>
        <li>
          Não brigue. É frustrante mas frequentemente é direito do
          proprietário escolher.
        </li>
        <li>
          Procure imobiliárias que abertamente atendem estrangeiros (ver lista acima).
        </li>
        <li>
          Se você tem japonês razoável e visto longo, mencione isso — diminui
          a barreira percebida.
        </li>
        <li>
          Famílias com filhos têm melhor chance que solteiros — proprietários
          enxergam como mais estáveis.
        </li>
      </UL>

      <H2 id="onde-procurar">Onde procurar</H2>
      <UL>
        <li>
          <ExternalLink href="https://suumo.jp/">Suumo</ExternalLink> — maior
          site de aluguel do Japão. Filtra por estrangeiro permitido (外国人OK).
        </li>
        <li>
          <ExternalLink href="https://www.homes.co.jp/">Homes.co.jp</ExternalLink>{" "}
          — segundo maior
        </li>
        <li>
          <ExternalLink href="https://gaijinpot.com/housing">GaijinPot Housing</ExternalLink>{" "}
          — focado em estrangeiros, em inglês
        </li>
        <li>
          <ExternalLink href="https://www.realestate.co.jp/">RealEstate.co.jp</ExternalLink>{" "}
          — em inglês
        </li>
        <li>Imobiliárias presenciais — chegue cedo, sem hora marcada, mostre disposição</li>
        <li>Facebook Marketplace / grupos locais brasileiros</li>
      </UL>

      <H2 id="dicas-finais">Dicas finais</H2>
      <UL>
        <li>
          <Strong>Visite antes de assinar.</Strong> Fotos enganam.
          Especialmente atenção a: ventilação, isolamento, vizinhos, distância
          do ponto de ônibus/trem.
        </li>
        <li>
          <Strong>Pergunte sobre kanrihi (管理費 — taxa de condomínio).</Strong>{" "}
          Apato simples: ¥0–3.000/mês. Manshon: ¥8.000–20.000/mês. Confunde
          o cálculo total.
        </li>
        <li>
          <Strong>Veja conta de luz/gás média do anterior:</Strong>{" "}
          apato mal isolado pode ter conta de inverno ¥15.000–25.000 (vs
          ¥6.000–10.000 em bom isolamento).
        </li>
        <li>
          <Strong>Cheque o WiFi/internet:</Strong> alguns apatos têm fibra,
          outros só ADSL. Importante se você trabalha remoto ou faz vídeo.
        </li>
        <li>
          <Strong>Fechadura magnética &gt; chave comum:</Strong> mais segura,
          algumas imobiliárias substituem mediante taxa pequena.
        </li>
        <li>
          <Strong>Estacionamento separado:</Strong> apato no Japão
          frequentemente NÃO vem com vaga. Estacionamento perto custa
          ¥5.000–25.000/mês adicional. Pergunte antes.
        </li>
        <li>
          <Strong>Não assine contrato sem ler tudo.</Strong> Use tradutor
          Google se precisar. Cláusulas sobre quanto te cobram pra sair antes
          do prazo, taxa de limpeza no checkout, regras de lixo (importantíssimo
          no Japão) — tudo isso está no contrato.
        </li>
      </UL>

      <H2 id="conclusao">Conclusão</H2>
      <P>
        Encontrar apato no Japão sendo brasileiro é difícil mas factível.
        A estratégia padrão:
      </P>
      <OL>
        <li>
          Comece no apato da empreiteira (sem entrada, mas caro e
          desconfortável)
        </li>
        <li>
          Trabalhe 3-6 meses, junte ¥300.000–500.000 de reserva
        </li>
        <li>
          Procure UR ou apato &ldquo;zero-zero&rdquo; ou imobiliária amiga
          de brasileiro
        </li>
        <li>
          Saia da empreiteira pra sua casa de verdade
        </li>
      </OL>
      <P>
        A diferença de qualidade de vida entre apato compartilhado da
        empreiteira e seu próprio cantinho é enorme — vale o esforço da
        reserva inicial.
      </P>
    </GuideLayout>
  );
}
